import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Skeleton,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { membersAPI } from '../api/api';
import { formatCurrency } from '../config';
import { useAuth } from '../contexts/AuthContext';

const Members = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user } = useAuth();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        pvc_pipes: '',
        pepper_plants: '',
        email: '',
        phone: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');

    useEffect(() => {
        fetchMembers();
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const response = await membersAPI.getAll();
            if (response.data.success) {
                setMembers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            toast.error('Failed to load members');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (member = null) => {
        if (member) {
            setEditingMember(member);
            setFormData({
                name: member.name,
                pvc_pipes: member.pvc_pipes,
                pepper_plants: member.pepper_plants,
                email: member.email || '',
                phone: member.phone || ''
            });
        } else {
            setEditingMember(null);
            setFormData({ name: '', pvc_pipes: '', pepper_plants: '', email: '', phone: '' });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingMember(null);
        setFormData({ name: '', pvc_pipes: '', pepper_plants: '', email: '', phone: '' });
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Name is required');
            return;
        }

        try {
            const payload = {
                ...formData,
                pvc_pipes: parseInt(formData.pvc_pipes) || 0,
                pepper_plants: parseInt(formData.pepper_plants) || 0
            };

            if (editingMember) {
                await membersAPI.update(editingMember.id, payload);
                toast.success('Member updated');
            } else {
                await membersAPI.create(payload);
                toast.success('Member added');
            }
            handleCloseDialog();
            fetchMembers();
        } catch (error) {
            toast.error('Failed to save member');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Member?',
            text: 'This may deactivate the member if they have transactions.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });

        if (result.isConfirmed) {
            try {
                await membersAPI.delete(id);
                toast.success('Member deleted/deactivated');
                fetchMembers();
            } catch (error) {
                toast.error('Failed to delete member');
            }
        }
    };

    // Filter members
    const filteredMembers = members
        .filter(m =>
            m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (m.phone && m.phone.includes(searchTerm))
        )
        .sort((a, b) => {
            const isAsc = order === 'asc';
            const valueA = a[orderBy] || '';
            const valueB = b[orderBy] || '';

            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return isAsc ? valueA - valueB : valueB - valueA;
            }

            const strA = String(valueA).toLowerCase();
            const strB = String(valueB).toLowerCase();

            if (strA < strB) return isAsc ? -1 : 1;
            if (strA > strB) return isAsc ? 1 : -1;
            return 0;
        });

    // Calculate totals
    const totalPipes = members.reduce((sum, m) => sum + (m.pvc_pipes || 0), 0);
    const totalPlants = members.reduce((sum, m) => sum + (m.pepper_plants || 0), 0);

    // Mobile Card View
    const MemberCard = ({ member }) => (
        <Card sx={{ mb: 2 }} className="hover-lift">
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {member.name}
                        </Typography>
                        {!member.is_active && (
                            <Chip label="Inactive" size="small" color="default" sx={{ mt: 0.5 }} />
                        )}
                    </Box>
                    {user && user.role !== 'VIEWER' && (
                        <Box>
                            <IconButton size="small" onClick={() => handleOpenDialog(member)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(member.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">PVC Pipes</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {member.pvc_pipes} ({totalPipes > 0 ? ((member.pvc_pipes / totalPipes) * 100).toFixed(1) : 0}%)
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Pepper Plants</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {member.pepper_plants} ({totalPlants > 0 ? ((member.pepper_plants / totalPlants) * 100).toFixed(1) : 0}%)
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Expenses</Typography>
                        <Typography variant="body2" color="error.main">
                            {formatCurrency(member.total_expenses || 0)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Income</Typography>
                        <Typography variant="body2" color="success.main">
                            {formatCurrency(member.total_income || 0)}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                    Members
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, maxWidth: { md: 400 }, ml: { md: 4 } }}>
                    <TextField
                        fullWidth
                        placeholder="Search partners..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon size="small" color="action" sx={{ mr: 1 }} />
                        }}
                    />
                </Box>
                {user && user.role !== 'VIEWER' && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add Member
                    </Button>
                )}
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                    <Card className="kpi-card info">
                        <CardContent>
                            <Typography className="kpi-label">Total Members</Typography>
                            <Typography className="kpi-value">{members.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card className="kpi-card primary">
                        <CardContent>
                            <Typography className="kpi-label">Total PVC Pipes</Typography>
                            <Typography className="kpi-value">{totalPipes}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card className="kpi-card secondary">
                        <CardContent>
                            <Typography className="kpi-label">Total Pepper Plants</Typography>
                            <Typography className="kpi-value">{totalPlants}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card className="kpi-card success">
                        <CardContent>
                            <Typography className="kpi-label">Active Members</Typography>
                            <Typography className="kpi-value">
                                {members.filter(m => m.is_active).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Content */}
            {loading ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={100} sx={{ mb: 1 }} />)
            ) : isMobile ? (
                <Box>
                    {filteredMembers.map((member) => (
                        <MemberCard key={member.id} member={member} />
                    ))}
                </Box>
            ) : (
                <Card className="next-gen-table">
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'name'}
                                            direction={orderBy === 'name' ? order : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'pvc_pipes'}
                                            direction={orderBy === 'pvc_pipes' ? order : 'asc'}
                                            onClick={() => handleRequestSort('pvc_pipes')}
                                        >
                                            PVC Pipes
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'pepper_plants'}
                                            direction={orderBy === 'pepper_plants' ? order : 'asc'}
                                            onClick={() => handleRequestSort('pepper_plants')}
                                        >
                                            Pepper Plants
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'total_expenses'}
                                            direction={orderBy === 'total_expenses' ? order : 'asc'}
                                            onClick={() => handleRequestSort('total_expenses')}
                                        >
                                            Total Expenses
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'total_income'}
                                            direction={orderBy === 'total_income' ? order : 'asc'}
                                            onClick={() => handleRequestSort('total_income')}
                                        >
                                            Total Income
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'balance'}
                                            direction={orderBy === 'balance' ? order : 'asc'}
                                            onClick={() => handleRequestSort('balance')}
                                        >
                                            Balance
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredMembers.map((member) => (
                                    <TableRow
                                        key={member.id}
                                        hover
                                        onClick={() => handleOpenDialog(member)}
                                        className="MuiTableRow-hover"
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography fontWeight={500}>{member.name}</Typography>
                                                    {member.phone && (
                                                        <Typography variant="caption" color="text.secondary">{member.phone}</Typography>
                                                    )}
                                                </Box>
                                                {user && user.role !== 'VIEWER' && (
                                                    <Box className="row-actions">
                                                        <IconButton
                                                            className="action-icon-btn edit"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleOpenDialog(member); }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            className="action-icon-btn delete"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(member.id); }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            {member.pvc_pipes}
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                ({totalPipes > 0 ? ((member.pvc_pipes / totalPipes) * 100).toFixed(1) : 0}%)
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {member.pepper_plants}
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                ({totalPlants > 0 ? ((member.pepper_plants / totalPlants) * 100).toFixed(1) : 0}%)
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>
                                            {formatCurrency(member.total_expenses || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>
                                            {formatCurrency(member.total_income || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{
                                            color: (member.balance || 0) >= 0 ? 'success.main' : 'error.main',
                                            fontWeight: 600
                                        }}>
                                            {formatCurrency(member.balance || 0)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={member.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={member.is_active ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}

                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingMember ? 'Edit Member' : 'Add Member'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="PVC Pipes"
                                type="number"
                                value={formData.pvc_pipes}
                                onChange={(e) => setFormData({ ...formData, pvc_pipes: e.target.value })}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Pepper Plants"
                                type="number"
                                value={formData.pepper_plants}
                                onChange={(e) => setFormData({ ...formData, pepper_plants: e.target.value })}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {editingMember ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Members;
